import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BellSimpleRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Mood-shift alert rule for a single symbol. Ping when the LATEST bias
 * snapshot's bull % (or bear %) crosses the chosen threshold. Deduped 12h
 * by the backend scheduler.
 */
export default function SentimentAlertButton({ symbol }) {
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [threshold, setThreshold] = useState(60);
  const [side, setSide] = useState("bullish");

  useEffect(() => {
    if (!open) return;
    api.get("/market/sentiment/alerts")
      .then((r) => {
        const rule = (r.data.rules || []).find((x) => x.symbol === symbol);
        setExisting(rule || null);
        if (rule) {
          setThreshold(rule.threshold_pct);
          setSide(rule.side);
        }
      })
      .catch(() => setExisting(null));
  }, [open, symbol]);

  const save = async (active = true) => {
    try {
      await api.post(`/market/sentiment/alert/${symbol}`, {
        threshold_pct: Number(threshold) || 0,
        side,
        active,
      });
      toast.success(active ? `Mood alert saved for ${symbol}` : `Mood alert cleared`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`sentiment-alert-btn-${symbol}`}
          title="Mood-shift alert"
          className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1"
        >
          <BellSimpleRinging size={11} /> Alert
        </button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm text-white" data-testid={`sentiment-alert-dialog-${symbol}`}>
        <DialogHeader>
          <DialogTitle className="mono">{symbol} mood alert</DialogTitle>
          <DialogDescription className="mono text-xs text-zinc-500">
            Ping me when the latest bias crosses my threshold. 12h cooldown.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Presets</div>
            <div className="grid grid-cols-3 gap-1">
              {[
                { side: "bullish", threshold: 60, label: "Bull 60%" },
                { side: "bullish", threshold: 75, label: "Bull 75%" },
                { side: "bearish", threshold: 60, label: "Bear 60%" },
              ].map((p) => (
                <button
                  key={p.label}
                  onClick={() => { setSide(p.side); setThreshold(p.threshold); }}
                  data-testid={`sentiment-preset-${p.side}-${p.threshold}`}
                  className={`mono text-[10px] uppercase tracking-widest py-1.5 rounded-sm border ${
                    side === p.side && Number(threshold) === p.threshold
                      ? p.side === "bullish"
                        ? "bg-[#00C805]/15 text-[#00C805] border-[#00C805]/40"
                        : "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Side</div>
            <div className="flex gap-1">
              {["bullish", "bearish"].map((s) => (
                <button
                  key={s}
                  onClick={() => setSide(s)}
                  data-testid={`sentiment-alert-side-${symbol}-${s}`}
                  className={`flex-1 mono text-[10px] uppercase tracking-widest py-2 rounded-sm border ${
                    side === s
                      ? s === "bullish"
                        ? "bg-[#00C805]/15 text-[#00C805] border-[#00C805]/40"
                        : "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40"
                      : "border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"
                  }`}
                >
                  {s === "bullish" ? "▲ Bull" : "▼ Bear"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Threshold %</div>
            <Input
              type="number"
              step="1"
              min="1"
              max="100"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              data-testid={`sentiment-alert-threshold-${symbol}`}
              className="bg-zinc-900 border-zinc-800 mono"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => save(true)}
              data-testid={`sentiment-alert-save-${symbol}`}
              className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-sm mono text-[10px] uppercase tracking-widest"
            >
              Save
            </Button>
            {existing && (
              <Button
                onClick={() => save(false)}
                data-testid={`sentiment-alert-clear-${symbol}`}
                variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
